import { RiBookmark3Line, RiCalendarScheduleLine, RiHistoryLine } from "@remixicon/react";
import classNames from "classnames";
import React, { useCallback, useMemo } from "react";
import {
    ArticleVersion,
    GetArticleDocument,
    ListArticleVersionsDocument,
    ListArticleVersionsQueryResult,
    ListProposalArticlesDocument,
    useDeleteArticleVersionMutation,
    useListArticleVersionsQuery,
} from "../../../../__generated__/api";
import useDeleteConfirm from "../../../../components/confirm/Delete";
import Button from "../../../../components/theme/button/Button";
import RouterLink from "../../../../components/theme/button/RouterLink";
import { useFormLoadedDrawer } from "../../../../components/theme/drawer";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import Icon from "../../../../components/theme/icon/Icon";
import Text from "../../../../components/theme/text/Text";
import Time from "../../../../components/time/Time";
import useRouting, { Paths } from "../../../../contexts/Routing";
import { tryParseDate } from "../../../../date";
import Styles from "./Versions.module.scss";

function VersionList({ id, versions, currentVersionId }: {
    id: string;
    versions: ArticleVersion[];
    currentVersionId: string | undefined
}) {
    const sorted = useMemo(() => versions.sortByDescending(row => tryParseDate(row.createdDate)), [versions]);
    const [deleteVersion] = useDeleteArticleVersionMutation();
    const { historyPush } = useRouting();
    const { element, setDeleting } = useDeleteConfirm<ArticleVersion>(async item => {
        const isLast = versions.length === 1;
        await deleteVersion({
            refetchQueries: [
                id ? {
                    query: GetArticleDocument,
                    variables: { id },
                } : undefined,
                {
                    query: ListArticleVersionsDocument,
                    variables: { id },
                },
                item.isProposed ? {
                    query: ListProposalArticlesDocument,
                } : undefined,
            ].filterNull(),
            variables: {
                id: item.id,
            },
        });

        if (currentVersionId === item.id) {
            historyPush(Paths.ANSWERS_GET, { id });
        } else if (isLast) {
            historyPush(Paths.ANSWERS_LIST);
        }
    }, item => tryParseDate(item.createdDate as string)?.toLocaleString());

    return (
        <div className={Styles.versions}>
            {element}
            {sorted.map(version => {
                const publishedDate = tryParseDate(version.publishedDate);
                const isFuturePublish = (publishedDate?.getTime() ?? 0) > Date.now();

                return (
                    <RouterLink
                        to={Paths.ANSWERS_GET_VERSION}
                        params={{ versionId: version.id, id }}
                        variant="none"
                        className={classNames(
                            Styles.version,
                            currentVersionId === version.id ? Styles["version-selected"] : undefined,
                        )}
                        key={version.id}
                    >
                        <div className={Styles["version-content"]}>
                            <div className={Styles["version-header"]}>
                                <Text textVariant="body-2-medium">
                                    <Time date={version.createdDate} variant="date-time" />
                                </Text>
                                {version.isLatestPublished && (
                                    <Text textVariant="body-2-regular" colorVariant="text-link">
                                        {" • "}
                                        Live
                                    </Text>
                                )}

                                {version.isProposed && (
                                    <Text textVariant="body-2-regular">
                                        {" • "}
                                        Proposed
                                    </Text>
                                )}
                            </div>
                            <Text textVariant="body-2-regular" colorVariant="body-secondary">
                                {version.creator ? (
                                    <>
                                        {version.creator?.name}
                                        {" "}
                                        created
                                    </>
                                ) : (
                                    <>
                                        <Icon.ArtificialIntelligence size="xsmall" />
                                        {" "}
                                        AI Generated
                                    </>
                                )}

                            </Text>

                            {version.publishedBy && publishedDate && (
                                <Text textVariant="body-2-regular" colorVariant="success">
                                    <Icon
                                        icon={isFuturePublish ? <RiCalendarScheduleLine /> : <RiBookmark3Line />}
                                        size="xsmall"
                                        variant="success"
                                    />
                                    {" "}
                                    {isFuturePublish ? "Publish scheduled by" : "Published By"}
                                    {" "}
                                    {version.publishedBy.name}
                                    {" on "}
                                    <Time date={version.publishedDate} variant="date-time" />
                                </Text>
                            )}
                        </div>
                        <Dropdown>
                            <Dropdown.Toggle
                                shouldStopPropagation
                                prefixIcon={
                                    <Icon.More />
                                }
                                variant="none"
                                size="small"
                            />
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => setDeleting(version)} prefixIcon={<Icon.Delete variant="danger" />}>
                                    <Text colorVariant="danger" textVariant="body-2-medium">
                                        Delete
                                    </Text>
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                    </RouterLink>
                );
            })}
        </div>
    );
}

export default function useVersions({
    id, currentVersionId, onClick, variant,
}: {
    id: string;
    currentVersionId: string | undefined;
    onClick: () => void;
    variant: "button" | "dropdown";
}) {
    const bodyCreator = useCallback(
        (data: ListArticleVersionsQueryResult) => (
            <VersionList
                id={id}
                currentVersionId={currentVersionId}
                versions={data.data?.articleVersions as ArticleVersion[]}
            />
        ),
        [id, currentVersionId],
    );
    const { element, setOpen } = useFormLoadedDrawer({
        query: useListArticleVersionsQuery,
        placement: "start",
        queryOptions: {
            skip: id == null,
            variables: {
                id,
            },
        },
        dependencies: [id, currentVersionId],
        title: "Version History",
        className: Styles["version-drawer"],
        bodyCreator,
    });
    return {
        element,
        button: (variant === "button" ? (
            <Button
                onClick={() => {
                    setOpen(true);
                    onClick();
                }}
                variant="outline-gray"
                size="small"
                prefixIcon={<Icon icon={<RiHistoryLine />} />}
            />
        ) : (
            <Dropdown.Item
                prefixIcon={<Icon icon={<RiHistoryLine />} />}
                onClick={() => {
                    setOpen(true);
                    onClick();
                }}
            >
                Verisons
            </Dropdown.Item>
        )),
    };
}
